import './Index.css';
import { make } from '../../utilities/Utilities';

/**
 * Base Paragraph Block for the Editor.js.
 * Represents simple paragraph
 *
 * @author CodeX (team@codex.so)
 * @copyright CodeX 2018
 * @license The MIT License (MIT)
 */

/**
 * @typedef {object} ParagraphConfig
 * @property {string} placeholder - placeholder for the empty paragraph
 * @property {boolean} preserveBlank - Whether or not to keep blank paragraphs when saving editor data
 */

/**
 * @typedef {Object} ParagraphData
 * @description Tool's input and output data format
 * @property {String} text — Paragraph's content. Can include HTML tags: <a><b><i>
 */
export default class TextBox {
    /**
     * Default placeholder for Paragraph Tool
     *
     * @return {string}
     * @constructor
     */
    static get DEFAULT_DATA() {
        return {
            input_placeholder: 'Text here...',
            text: '<p><br></p>',
            stretched: false,
            largeBox: false,
            boxDirection: 'left',
            textAlignment: 'left'
        }
    }

    /**
     * Render plugin`s main Element and fill it with saved data
     *
     * @param {object} params - constructor params
     * @param {ParagraphData} params.data - previously saved data
     * @param {ParagraphConfig} params.config - user config for Tool
     * @param {object} params.api - editor.js api
     * @param {boolean} readOnly - read only mode flag
     */
    constructor({ data, config, api, readOnly }) {
        this.api = api;
        this.readOnly = readOnly;

        this._CSS = {
            block: this.api.styles.block,
            wrapper: ['ce-textbox', 'my-5', 'px-0'],
            textHolder: ['text-paragraph', 'cdx-textinput'],
            settingsButton: this.api.styles.settingsButton,
            settingsButtonActive: this.api.styles.settingsButtonActive,
        };

        this.nodes = {
            wrapper: null,
            container: null,
            textHolder: null,
        };

        if (!this.readOnly) {
            this.onKeyUp = this.onKeyUp.bind(this);
        }

        this.blockIndex = this.api.blocks.getCurrentBlockIndex() + 1;

        /**
         * Placeholder for paragraph if it is first Block
         * @type {string}
         */
        this._placeholder = config.placeholder ? config.placeholder : TextBox.DEFAULT_DATA.input_placeholder;

        this._data = {};
        this.data = data;

        this._preserveBlank = config.preserveBlank !== undefined ? config.preserveBlank : false;

        this.settings = [
            {
                name: 'stretched',
                toggle: true,
                title: 'Stretched',
                icon: '<svg width="17" height="10" viewBox="0 0 17 10" xmlns="http://www.w3.org/2000/svg"><path d="M13.568 5.925H4.056l1.703 1.703a1.125 1.125 0 0 1-1.59 1.591L.962 6.014A1.069 1.069 0 0 1 .588 4.26L4.38.469a1.069 1.069 0 0 1 1.512 1.511L4.084 3.787h9.606l-1.85-1.85a1.069 1.069 0 1 1 1.512-1.51l3.792 3.791a1.069 1.069 0 0 1-.475 1.788L13.514 9.16a1.125 1.125 0 0 1-1.59-1.591l1.644-1.644z"/></svg>',
            },
            {
                name: 'largeBox',
                toggle: true,
                title: 'Large box',
                icon: '<svg fill="none" height="22" width="22" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><g><path d="M31.49072,1.90784C31.46204,1.13294,30.76996,0.49829,30,0.5h-7.98633c-1.96869,0.034-1.97394,2.96533,0.00006,3   c-0.00006,0,4.36517,0,4.36517,0l-7.8291,7.8291c-0.58594,0.58594-0.58594,1.53516,0,2.12109s1.53516,0.58594,2.12109,0   L28.5,5.6211v4.36523c0.03174,1.96783,2.96667,1.974,3-0.00006C31.5,9.98633,31.5,2,31.5,2   C31.5,1.96845,31.49268,1.93891,31.49072,1.90784z"/><path d="M25.80176,11.91797c-0.82813,0-1.5,0.67188-1.5,1.5v13.01367c0,1.14063-0.92773,2.06836-2.06836,2.06836H5.56836   C4.42773,28.5,3.5,27.57227,3.5,26.43164V9.7666c0-1.14063,0.92773-2.06836,2.06836-2.06836h13.01367   c0.82813,0,1.5-0.67188,1.5-1.5s-0.67188-1.5-1.5-1.5H5.56836C2.77344,4.69824,0.5,6.97168,0.5,9.7666v16.66504   C0.5,29.22656,2.77344,31.5,5.56836,31.5H22.2334c2.79492,0,5.06836-2.27344,5.06836-5.06836V13.41797   C27.30176,12.58985,26.62988,11.91797,25.80176,11.91797z"/></g></svg>',
            },
            {
                name: 'boxDirection',
                toggle: 'boxDirection',
                value: 'left',
                title: 'Text box to the left',
                icon: '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M16.6743 9.1716L15.26 7.75739L11.0174 12L15.26 16.2426L16.6742 14.8284L13.8458 12L16.6743 9.1716Z" fill="currentColor"/><path d="M9.96265 8.00007V16.0001H7.96265V8.00007H9.96265Z" fill="currentColor"/><path clip-rule="evenodd" d="M1 5C1 2.79086 2.79086 1 5 1H19C21.2091 1 23 2.79086 23 5V19C23 21.2091 21.2091 23 19 23H5C2.79086 23 1 21.2091 1 19V5ZM5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" fill="currentColor" fill-rule="evenodd"/></svg>',
            },
            {
                name: 'boxDirection',
                toggle: 'boxDirection',
                value: 'center',
                title: 'Center the text box',
                icon: '<svg fill="none" width="24" height="24" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><g transform="matrix(1.1008 0 0 1.1008 499.1648 499.1648)"><g style=""><g transform="matrix(37.8788 0 0 37.8788 183.724 0.0001)"><path transform="translate(-13.8458, -12)" d="M 16.6743 9.1716 L 15.26 7.75739 L 11.0174 12 L 15.26 16.2426 L 16.6742 14.8284 L 13.8458 12 L 16.6743 9.1716 Z" fill="currentColor"/></g><g transform="matrix(37.8788 0 0 37.8788 0 0.0001)"><path transform=" translate(-8.9627, -12.0001)" d="M 9.96265 8.00007 V 16.0001 H 7.96265 V 8.00007 H 9.96265 Z" fill="currentColor"/></g><g transform="matrix(37.8788 0 0 37.8788 0.0001 0.0001)"><path transform=" translate(-12, -12)" d="M 1 5 C 1 2.79086 2.79086 1 5 1 H 19 C 21.2091 1 23 2.79086 23 5 V 19 C 23 21.2091 21.2091 23 19 23 H 5 C 2.79086 23 1 21.2091 1 19 V 5 Z M 5 3 H 19 C 20.1046 3 21 3.89543 21 5 V 19 C 21 20.1046 20.1046 21 19 21 H 5 C 3.89543 21 3 20.1046 3 19 V 5 C 3 3.89543 3.89543 3 5 3 Z" clip-rule="evenodd" fill="currentColor" fill-rule="evenodd"/></g><g transform="matrix(-37.8788 0 0 -37.8788 -184.5713 0)"><path transform=" translate(-13.8458, -12)" d="M 16.6743 9.1716 L 15.26 7.75739 L 11.0174 12 L 15.26 16.2426 L 16.6742 14.8284 L 13.8458 12 L 16.6743 9.1716 Z" fill="currentColor"/></g></g></g></svg>',
            },
            {
                name: 'boxDirection',
                toggle: 'boxDirection',
                value: 'right',
                title: 'Text box to the right',
                icon: '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7.64429 14.8284L9.0585 16.2426L13.3012 12L9.05853 7.75739L7.64432 9.1716L10.4727 12L7.64429 14.8284Z" fill="currentColor"/><path d="M14.3559 16L14.3559 7.99996H16.3559V16H14.3559Z" fill="currentColor"/><path clip-rule="evenodd" d="M1 5C1 2.79086 2.79086 1 5 1H19C21.2091 1 23 2.79086 23 5V19C23 21.2091 21.2091 23 19 23H5C2.79086 23 1 21.2091 1 19V5ZM5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" fill="currentColor" fill-rule="evenodd"/></svg>',
            },
            {
                name: 'textAlignment',
                toggle: 'textAlignment',
                value: 'left',
                title: 'Align text to left',
                icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M30 30h-24v4h24v-4zm0-16h-24v4h24v-4zm-24 12h36v-4h-36v4zm0 16h36v-4h-36v4zm0-36v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
            },
            {
                name: 'textAlignment',
                toggle: 'textAlignment',
                value: 'center',
                title: 'Align text to center',
                icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M14 30v4h20v-4h-20zm-8 12h36v-4h-36v4zm0-16h36v-4h-36v4zm8-12v4h20v-4h-20zm-8-8v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
            },
            {
                name: 'textAlignment',
                toggle: 'textAlignment',
                value: 'right',
                title: 'Align text to right',
                icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M6 42h36v-4h-36v4zm12-8h24v-4h-24v4zm-12-8h36v-4h-36v4zm12-8h24v-4h-24v4zm-12-12v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
            }
        ];
        /*
        this.settings = {
            general: [
                {
                    name: 'stretched',
                    title: 'Stretched',
                    icon: '<svg width="17" height="10" viewBox="0 0 17 10" xmlns="http://www.w3.org/2000/svg"><path d="M13.568 5.925H4.056l1.703 1.703a1.125 1.125 0 0 1-1.59 1.591L.962 6.014A1.069 1.069 0 0 1 .588 4.26L4.38.469a1.069 1.069 0 0 1 1.512 1.511L4.084 3.787h9.606l-1.85-1.85a1.069 1.069 0 1 1 1.512-1.51l3.792 3.791a1.069 1.069 0 0 1-.475 1.788L13.514 9.16a1.125 1.125 0 0 1-1.59-1.591l1.644-1.644z"/></svg>',
                },
                {
                    name: 'largeBox',
                    title: 'Large box',
                    icon: '<svg fill="none" height="22" width="22" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><g><path d="M31.49072,1.90784C31.46204,1.13294,30.76996,0.49829,30,0.5h-7.98633c-1.96869,0.034-1.97394,2.96533,0.00006,3   c-0.00006,0,4.36517,0,4.36517,0l-7.8291,7.8291c-0.58594,0.58594-0.58594,1.53516,0,2.12109s1.53516,0.58594,2.12109,0   L28.5,5.6211v4.36523c0.03174,1.96783,2.96667,1.974,3-0.00006C31.5,9.98633,31.5,2,31.5,2   C31.5,1.96845,31.49268,1.93891,31.49072,1.90784z"/><path d="M25.80176,11.91797c-0.82813,0-1.5,0.67188-1.5,1.5v13.01367c0,1.14063-0.92773,2.06836-2.06836,2.06836H5.56836   C4.42773,28.5,3.5,27.57227,3.5,26.43164V9.7666c0-1.14063,0.92773-2.06836,2.06836-2.06836h13.01367   c0.82813,0,1.5-0.67188,1.5-1.5s-0.67188-1.5-1.5-1.5H5.56836C2.77344,4.69824,0.5,6.97168,0.5,9.7666v16.66504   C0.5,29.22656,2.77344,31.5,5.56836,31.5H22.2334c2.79492,0,5.06836-2.27344,5.06836-5.06836V13.41797   C27.30176,12.58985,26.62988,11.91797,25.80176,11.91797z"/></g></svg>',
                }
            ],
            boxDirections: [
                {
                    value: 'left',
                    name: 'boxDirection',
                    title: 'Text box to the left',
                    icon: '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M16.6743 9.1716L15.26 7.75739L11.0174 12L15.26 16.2426L16.6742 14.8284L13.8458 12L16.6743 9.1716Z" fill="currentColor"/><path d="M9.96265 8.00007V16.0001H7.96265V8.00007H9.96265Z" fill="currentColor"/><path clip-rule="evenodd" d="M1 5C1 2.79086 2.79086 1 5 1H19C21.2091 1 23 2.79086 23 5V19C23 21.2091 21.2091 23 19 23H5C2.79086 23 1 21.2091 1 19V5ZM5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" fill="currentColor" fill-rule="evenodd"/></svg>',
                },
                {
                    value: 'center',
                    name: 'boxDirection',
                    title: 'Center the text box',
                    icon: '<svg fill="none" width="24" height="24" viewBox="0 0 1000 1000" xmlns="http://www.w3.org/2000/svg"><g transform="matrix(1.1008 0 0 1.1008 499.1648 499.1648)"><g style=""><g transform="matrix(37.8788 0 0 37.8788 183.724 0.0001)"><path transform="translate(-13.8458, -12)" d="M 16.6743 9.1716 L 15.26 7.75739 L 11.0174 12 L 15.26 16.2426 L 16.6742 14.8284 L 13.8458 12 L 16.6743 9.1716 Z" fill="currentColor"/></g><g transform="matrix(37.8788 0 0 37.8788 0 0.0001)"><path transform=" translate(-8.9627, -12.0001)" d="M 9.96265 8.00007 V 16.0001 H 7.96265 V 8.00007 H 9.96265 Z" fill="currentColor"/></g><g transform="matrix(37.8788 0 0 37.8788 0.0001 0.0001)"><path transform=" translate(-12, -12)" d="M 1 5 C 1 2.79086 2.79086 1 5 1 H 19 C 21.2091 1 23 2.79086 23 5 V 19 C 23 21.2091 21.2091 23 19 23 H 5 C 2.79086 23 1 21.2091 1 19 V 5 Z M 5 3 H 19 C 20.1046 3 21 3.89543 21 5 V 19 C 21 20.1046 20.1046 21 19 21 H 5 C 3.89543 21 3 20.1046 3 19 V 5 C 3 3.89543 3.89543 3 5 3 Z" clip-rule="evenodd" fill="currentColor" fill-rule="evenodd"/></g><g transform="matrix(-37.8788 0 0 -37.8788 -184.5713 0)"><path transform=" translate(-13.8458, -12)" d="M 16.6743 9.1716 L 15.26 7.75739 L 11.0174 12 L 15.26 16.2426 L 16.6742 14.8284 L 13.8458 12 L 16.6743 9.1716 Z" fill="currentColor"/></g></g></g></svg>',
                },
                {
                    value: 'right',
                    name: 'boxDirection',
                    title: 'Text box to the right',
                    icon: '<svg fill="none" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7.64429 14.8284L9.0585 16.2426L13.3012 12L9.05853 7.75739L7.64432 9.1716L10.4727 12L7.64429 14.8284Z" fill="currentColor"/><path d="M14.3559 16L14.3559 7.99996H16.3559V16H14.3559Z" fill="currentColor"/><path clip-rule="evenodd" d="M1 5C1 2.79086 2.79086 1 5 1H19C21.2091 1 23 2.79086 23 5V19C23 21.2091 21.2091 23 19 23H5C2.79086 23 1 21.2091 1 19V5ZM5 3H19C20.1046 3 21 3.89543 21 5V19C21 20.1046 20.1046 21 19 21H5C3.89543 21 3 20.1046 3 19V5C3 3.89543 3.89543 3 5 3Z" fill="currentColor" fill-rule="evenodd"/></svg>',
                }
            ],
            textAlignments: [
                {
                    value: 'left',
                    name: 'textAlignment',
                    title: 'Align text to left',
                    icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M30 30h-24v4h24v-4zm0-16h-24v4h24v-4zm-24 12h36v-4h-36v4zm0 16h36v-4h-36v4zm0-36v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
                },
                {
                    value: 'center',
                    name: 'textAlignment',
                    title: 'Align text to center',
                    icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M14 30v4h20v-4h-20zm-8 12h36v-4h-36v4zm0-16h36v-4h-36v4zm8-12v4h20v-4h-20zm-8-8v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
                },
                {
                    value: 'right',
                    name: 'textAlignment',
                    title: 'Align text to right',
                    icon: '<svg height="20" viewBox="0 0 48 48" width="20" xmlns="http://www.w3.org/2000/svg"><path d="M6 42h36v-4h-36v4zm12-8h24v-4h-24v4zm-12-8h36v-4h-36v4zm12-8h24v-4h-24v4zm-12-12v4h36v-4h-36z"/><path d="M0 0h48v48h-48z" fill="none"/></svg>',
                }
            ]
        };*/
        //this.settingsButtons = [];
    }

    /**
     * Check if text content is empty and set empty string to inner html.
     * We need this because some browsers (e.g. Safari) insert <br> into empty contenteditanle elements
     *
     * @param {KeyboardEvent} e - key up event
     */
    onKeyUp(e) {
        e = e || window.event

        if (e.key === 'Enter' || e.keyCode === 13) {
            document.execCommand('formatBlock', false, 'p');
            return false;
        }

        /*
        if (e.code !== 'Backspace' && e.code !== 'Delete') {
            return;
        }

        const { textContent } = this._element;

        if (textContent === '') {
            this._element.innerHTML = '';
        }*/
    }

    /**
     * Return Tool's view
     *
     * @returns {HTMLDivElement}
     */
    render() {
        const wrapper = make('div', [this._CSS.block, ...this._CSS.wrapper]);

        const container = make('div', ['px-0', 'w-100']);

        const textHolder = make('div', this._CSS.textHolder);
        textHolder.dataset.placeholder = this.api.i18n.t(this._placeholder);
        textHolder.contentEditable = false;
        if (!this.readOnly) {
            textHolder.contentEditable = true;
            textHolder.addEventListener('keyup', this.onKeyUp);
        }
        textHolder.innerHTML = this.data.text || TextBox.DEFAULT_DATA.text;


        container.appendChild(textHolder);
        wrapper.appendChild(container);

        this.nodes.wrapper = wrapper;
        this.nodes.container = container;
        this.nodes.textHolder = textHolder;

        this._acceptTuneView();

        return wrapper;
    }

    /**
     * Method that specified how to merge two Text blocks.
     * Called by Editor.js by backspace at the beginning of the Block
     * @param {ParagraphData} data
     * @public
     */
    merge(data) {
        let newData = {
            text: this.data.text + data.text,
            stretched: this.data.stretched,
            largeBox: this.data.largeBox,
            boxDirection: this.data.boxDirection,
            textAlignment: this.data.textAlignment
        };

        this.data = newData;
    }

    /**
     * Validate Paragraph block data:
     * - check for emptiness
     *
     * @param {ParagraphData} savedData — data received after saving
     * @returns {boolean} false if saved data is not correct, otherwise true
     * @public
     */
    validate(savedData) {
        if (savedData.text.trim() === '' && !this._preserveBlank) {
            return false;
        }

        return true;
    }

    normalizeData(data) {
        const newData = {};

        if (typeof data !== 'object') {
            data = {};
        }

        newData.text = data.text || TextBox.DEFAULT_DATA.text;
        newData.stretched = data.stretched !== undefined ? data.stretched : TextBox.DEFAULT_DATA.stretched;
        newData.largeBox = data.largeBox !== undefined ? data.largeBox : TextBox.DEFAULT_DATA.largeBox;
        newData.boxDirection = data.boxDirection !== undefined ? data.boxDirection : TextBox.DEFAULT_DATA.boxDirection;
        newData.textAlignment = data.textAlignment !== undefined ? data.textAlignment : TextBox.DEFAULT_DATA.textAlignment;
        return newData;
    }

    /**
     * Sanitizer rules
     */
    static get sanitize() {
        return {
            text: {
                p: {},
                br: {}
            },
            stretched: {},
            largeBox: {},
            boxDirection: {},
            textAlignment: {}
        };
    }

    /**
     * Extract Tool's data from the view
     * @param {HTMLDivElement} toolsContent - Paragraph tools rendered view
     * @returns {ParagraphData} - saved data
     * @public
     */
    save(toolsContent) {
        const textHolder = toolsContent.querySelector('.' + this._CSS.textHolder);

        if (!textHolder) {
            return this.data;
        }

        return Object.assign(this.data, {
            text: textHolder.innerHTML
        });
    }

    /**
     * On paste callback fired from Editor.
     *
     * @param {PasteEvent} event - event with pasted data
     */
    onPaste(event) {
        return Object.assign(this.data, {
            text: event.detail.data.innerHTML
        });

    }

    /**
     * Enable Conversion Toolbar. Paragraph can be converted to/from other tools
     */
    static get conversionConfig() {
        return {
            export: 'text', // to convert Paragraph to other block, use 'text' property of saved data
            import: 'text' // to covert other block's exported string to Paragraph, fill 'text' property of tool data
        };
    }


    /**
     * Returns true to notify the core that read-only mode is supported
     *
     * @return {boolean}
     */
    static get isReadOnlySupported() {
        return true;
    }

    static get enableLineBreaks() {
        return true;
    }

    /**
     * Get current Tools`s data
     * @returns {ParagraphData} Current data
     * @private
     */
    get data() {
        return this._data;
    }

    /**
     * Store data in plugin:
     * - at the this._data property
     * - at the HTML
     *
     * @param {ParagraphData} data — data to set
     * @private
     */
    set data(data) {
        this._data = Object.assign({}, this.data, data);
        this._data = this.normalizeData(this._data);

        if (this.nodes.textHolder) {
            this.nodes.textHolder.innerHTML = this._data.text || TextBox.DEFAULT_DATA.text;
        }
    }


    /**
     * Used by Editor paste handling API.
     * Provides configuration to handle P tags.
     *
     * @returns {{tags: string[]}}
     */
    static get pasteConfig() {
        return {
            tags: ['P']
        };
    }


    renderSettings() {
        //const tunes = this.settings.concat(this.actions);

        return this.settings.map(tune => ({
            icon: tune.icon,
            label: this.api.i18n.t(tune.title),
            name: tune.name,
            toggle: tune.toggle,
            isActive: (tune.value && (this.data[tune.name] === tune.value)) || (tune.toggle === true && this.data[tune.name]),
            onActivate: () => {
                if (typeof tune.action === 'function') {
                    this.tuneClicked(tune.name, tune.value, tune.action);
                    return;
                }
                this.tuneClicked(tune.name, tune.value);
            },
        }));
    }

    confirmTuneValueOrGetFirstValue(tuneName, tuneValue) {
        const confirmed = this.settings.find(item => item.name === tuneName && item.value === tuneValue);
        if (confirmed !== undefined) {
            return confirmed.value;
        }
        return this.settings.filter(item => item.name === tuneName)[0].value;
    }

    /*
    renderSettings() {
        this.settingsButtons = [];

        const wrapper = document.createElement('DIV');

        this.renderSettingSection(this.settings.general, this.data, wrapper);

        this.renderSettingSection(this.settings.boxDirections, this.data, wrapper);

        this.renderSettingSection(this.settings.textAlignments, this.data, wrapper);

        return wrapper;
    }

    renderSettingSection(tunes, toolData, wrapper) {
        const btnsContainer = wrapper.appendChild(make('div', 'cdx-settings-container'));

        tunes.forEach(tune => {
            const title = this.api.i18n.t(tune.title);
            const el = make('div', this._CSS.settingsButton, {
                innerHTML: tune.icon,
                title,
            });

            el.addEventListener('click', () => {
                this.tuneClicked(tune.name, tune.value, tune.action);
            });

            el.dataset.tuneName = tune.name;
            el.dataset.tuneValue = tune.value;

            el.classList.toggle(this._CSS.settingsButtonActive,
                (tune.value === undefined && this.confirmTuneValueOrGetFirstValue(tune.name, toolData[tune.name]) === true) ||
                (tune.value === this.confirmTuneValueOrGetFirstValue(tune.name, toolData[tune.name]))
            );

            this.settingsButtons.push(el);

            this.api.tooltip.onHover(el, title, {
                placement: 'top',
            });

            btnsContainer.appendChild(el);
        });

        wrapper.appendChild(btnsContainer);
        wrapper.appendChild(make('HR', 'cdx-settings-divider'));
    }

    /**
     * Clicks to one of the tunes
     *
     * @param {string} tuneName - clicked tune name
     * @param {Function} customFunction - function to execute on click
     */
    tuneClicked(tuneName, tuneValue, customFunction) {
        if (typeof customFunction === 'function') {
            if (!customFunction(tuneName)) {
                return false;
            }
        }

        if (tuneName === 'stretched' ||
            tuneName === 'largeBox') {
            this.data[tuneName] = !this.data[tuneName];
            //const button = this.settingsButtons.find(el => el.dataset.tuneName === tuneName);
            //button.classList.toggle(this._CSS.settingsButtonActive, !!this.data[tuneName]);
        } else {
            this.data[tuneName] = this.confirmTuneValueOrGetFirstValue(tuneName, tuneValue);
            //this.settingsButtons.forEach(button => {
            //    if (button.dataset.tuneName === tuneName) {
            //        button.classList.toggle(this._CSS.settingsButtonActive, button.dataset.tuneValue === tuneValue);
            //    }
            //});
        }

        this._acceptTuneView();
    }


    _acceptTuneView() {
        this.nodes.container.classList.toggle('container', !!!this.data.stretched);
        this.api.blocks.stretchBlock(this.blockIndex, !!this.data.stretched);

        this.nodes.textHolder.classList.toggle('lrg', !!this.data.largeBox);


        if (this.data.boxDirection === 'center') {
            this.nodes.textHolder.classList.toggle('ml-auto', true);
            this.nodes.textHolder.classList.toggle('mr-auto', true);
        } else if (this.data.boxDirection === 'right') {
            this.nodes.textHolder.classList.toggle('ml-auto', true);
            this.nodes.textHolder.classList.toggle('mr-auto', false);
        }
        else {
            this.nodes.textHolder.classList.toggle('ml-auto', false);
            this.nodes.textHolder.classList.toggle('mr-auto', true);
        }

        if (this.data.textAlignment === 'center') {
            this.nodes.textHolder.classList.toggle('text-center', true);
            this.nodes.textHolder.classList.toggle('text-right', false);
            this.nodes.textHolder.classList.toggle('text-left', false);
        } else if (this.data.textAlignment === 'right') {
            this.nodes.textHolder.classList.toggle('text-center', false);
            this.nodes.textHolder.classList.toggle('text-right', true);
            this.nodes.textHolder.classList.toggle('text-left', false);
        }
        else {
            this.nodes.textHolder.classList.toggle('text-center', false);
            this.nodes.textHolder.classList.toggle('text-right', false);
            this.nodes.textHolder.classList.toggle('text-left', true);
        }
    }

    /*
    confirmTuneValueOrGetFirstValue(tuneName, tuneValue) {
        if (tuneName === 'stretched') {
            if (tuneValue !== undefined)
                return tuneValue === true;
            return TextBox.DEFAULT_DATA.stretched;
        }
        else if (tuneName === 'largeBox') {
            if (tuneValue !== undefined)
                return tuneValue === true;
            return TextBox.DEFAULT_DATA.largeBox;
        } else if (tuneName === 'boxDirection') {
            return this._getTuneValueOrFirstValue(
                this.settings.boxDirections,
                tuneValue
            );
        } else if (tuneName === 'textAlignment') {
            return this._getTuneValueOrFirstValue(
                this.settings.textAlignments,
                tuneValue
            );
        }
        throw 'No tune can be confirmed';
    }

    _getTuneValueOrFirstValue(tunes, value) {
        if (!Array.isArray(tunes) || tunes.length <= 0) {
            throw 'Tune array is invalid or empty';
        }

        for (let i = 0; i < tunes.length; i++) {
            if (value === tunes[i].value) {
                return tunes[i].value;
            }
        }

        return tunes[0].value;
    }*/

    /**
     * Icon and title for displaying at the Toolbox
     *
     * @return {{icon: string, title: string}}
     */
    static get toolbox() {
        return {
            icon: '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0.2 -0.3 9 11.4" width="12" height="14"><path d="M0 2.77V.92A1 1 0 01.2.28C.35.1.56 0 .83 0h7.66c.28.01.48.1.63.28.14.17.21.38.21.64v1.85c0 .26-.08.48-.23.66-.15.17-.37.26-.66.26-.28 0-.5-.09-.64-.26a1 1 0 01-.21-.66V1.69H5.6v7.58h.5c.25 0 .45.08.6.23.17.16.25.35.25.6s-.08.45-.24.6a.87.87 0 01-.62.22H3.21a.87.87 0 01-.61-.22.78.78 0 01-.24-.6c0-.25.08-.44.24-.6a.85.85 0 01.61-.23h.5V1.7H1.73v1.08c0 .26-.08.48-.23.66-.15.17-.37.26-.66.26-.28 0-.5-.09-.64-.26A1 1 0 010 2.77z" /></svg>',
            title: 'Text box'
        };
    }
}